import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Play, Pause, Plus, SpinnerGap, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CampaignDetail } from "@/components/telecalling/CampaignDetail";

const iso = (d) => d.toISOString().slice(0, 10);
const TONE = { running: "bg-emerald-100 text-emerald-700", paused: "bg-amber-100 text-amber-700", done: "bg-slate-100 text-slate-600" };
const parse = (t) => t.split("\n").map((l) => l.split(/[,\t]/).map((x) => x.trim())).filter((p) => p[0]).map(([phone, company = "", owner = "", mc = ""]) => ({ phone, company, owner, mc }));

function NewList({ onDone }) {
  const [f, setF] = useState({ name: "", source: "new_mcs", date_from: iso(new Date(Date.now() - 2 * 864e5)), date_to: iso(new Date()), text: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const create = async () => {
    setBusy(true);
    try {
      const body = { name: f.name || `New MCs ${f.date_from} → ${f.date_to}`, source: f.source, ...(f.source === "new_mcs" ? { date_from: f.date_from, date_to: f.date_to } : { contacts: parse(f.text) }) };
      const { data } = await api.post("/telecalling/campaigns", body, { timeout: 120000 });
      toast.success(`${data.total} carrier${data.total === 1 ? "" : "s"} added`); onDone(data.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const seg = (on) => `h-9 px-3 rounded-lg border text-sm font-semibold ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600"}`;
  return (
    <Panel className="p-5 space-y-3" data-testid="tc-new-list">
      <p className="font-heading font-bold text-slate-900">New call list</p>
      <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="List name (optional)" className="bg-white border-slate-300" data-testid="tc-list-name" />
      <div className="flex gap-2"><button onClick={() => set("source", "new_mcs")} className={seg(f.source === "new_mcs")} data-testid="tc-src-new-mcs">New MCs from FMCSA</button><button onClick={() => set("source", "paste")} className={seg(f.source === "paste")} data-testid="tc-src-paste">Paste numbers</button></div>
      {f.source === "new_mcs" ? (
        <div className="grid grid-cols-2 gap-3">
          <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="bg-white border-slate-300" data-testid="tc-list-from" />
          <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="bg-white border-slate-300" data-testid="tc-list-to" />
          <p className="col-span-2 text-[11px] text-slate-400">Pulls every new “Motor Carrier of Property” MC FMCSA published in those dates that has a phone number.</p>
        </div>
      ) : (
        <Textarea rows={6} value={f.text} onChange={(e) => set("text", e.target.value)} placeholder={"One per line: phone, company, owner, MC\n(984) 555-0101, Eagle Freight LLC, John Smith, MC1234567"} className="bg-white border-slate-300 font-mono text-xs" data-testid="tc-list-paste" />
      )}
      <button onClick={create} disabled={busy} className="h-10 px-5 rounded-xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="tc-list-create">{busy ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={16} />}Create list</button>
    </Panel>
  );
}

export function CallLists() {
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/telecalling/campaigns").then(({ data }) => setLists(data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const act = async (id, action) => {
    if (action === "start" && !window.confirm("Start AI auto-dialing this list?\n\nI confirm these carriers may be called by an AI voice agent, and Do-Not-Call requests will be honored.")) return;
    try { await api.post(`/telecalling/campaigns/${id}/${action}`); toast.success(action === "start" ? "Dialing started" : "Paused"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (open) return <CampaignDetail id={open} onBack={() => { setOpen(null); load(); }} onAction={act} />;
  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-6">
      <Panel className="divide-y divide-slate-100" data-testid="tc-lists">
        {!lists.length && <p className="p-8 text-sm text-slate-400 text-center" data-testid="tc-lists-empty">No call lists yet. Create one to start dialing.</p>}
        {lists.map((l) => (
          <div key={l.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`tc-list-${l.id}`}>
            <button onClick={() => setOpen(l.id)} className="flex-1 min-w-[200px] text-left">
              <p className="font-semibold text-slate-900 inline-flex items-center gap-1">{l.name}<CaretRight size={12} /></p>
              <p className="text-xs text-slate-500">{l.total} carriers · {l.counts.done || 0} called · {l.counts.Interested || 0} interested · {l.counts.queued || 0} left</p>
              {l.note && <p className="text-[11px] text-amber-600 mt-0.5">{l.note}</p>}
            </button>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${TONE[l.status]}`}>{l.status}</span>
            {l.status !== "done" && (l.status === "running"
              ? <button onClick={() => act(l.id, "pause")} className="h-9 px-3 rounded-lg border border-slate-300 text-sm font-semibold inline-flex items-center gap-1" data-testid={`tc-pause-${l.id}`}><Pause size={14} />Pause</button>
              : <button onClick={() => act(l.id, "start")} className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1" data-testid={`tc-start-${l.id}`}><Play size={14} weight="fill" />Start dialing</button>)}
          </div>
        ))}
      </Panel>
      <NewList onDone={(id) => { load(); setOpen(id); }} />
    </div>
  );
}
